import {CalendarFile} from 'calendar/CalendarConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserUtil, {FileIconNameItem} from 'file-browser/FileBrowserUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {WorklistType, WORKLIST_TYPE_TO_ICON} from 'worklists/WorklistConstants';

/** Minimal file fields needed for homepage outline icon resolution. */
export type HomepageFileIconInput = Pick<CalendarFile, 'id' | 'tagSchema' | 'type'>;

const MODEL_TYPE_OUTLINE_ICONS: Partial<Record<string, BenchlingIconName>> = {
  [ModelTypeConstants.ENTRY]: 'file-outline',
  [ModelTypeConstants.PROTOCOL]: 'square-list-outline',
  [ModelTypeConstants.WORKSHEET]: 'file-lines-outline',
  [ModelTypeConstants.STAGE_ENTRY]: 'pen-to-square-outline',
  [ModelTypeConstants.TEMPLATE]: 'template',
  // No outline variant exists, so this matches the icon search and the sub-template editor use.
  [ModelTypeConstants.SUBTEMPLATE]: 'subtemplate',
};

/**
 * Outline-style icons for homepage surfaces (Recents, My Work, Worklists). Falls back to
 * {@link FileBrowserUtil.getFileIconName} when no outline mapping exists.
 */
export function getHomepageFileOutlineIcon(file: HomepageFileIconInput): BenchlingIconName {
  if (file.tagSchema != null) {
    const schemaOutlineIcon = MODEL_TYPE_OUTLINE_ICONS[file.tagSchema.folderItemType];
    if (schemaOutlineIcon != null) {
      return schemaOutlineIcon;
    }
  }

  const modelType = APIUtil.apiIdToMaybeType(file.id);
  const outlineIcon = modelType != null ? MODEL_TYPE_OUTLINE_ICONS[modelType] : undefined;
  if (outlineIcon != null) {
    return outlineIcon;
  }

  if (modelType == null) {
    return 'file-outline';
  }

  return FileBrowserUtil.getFileIconName(file as FileIconNameItem);
}

const ASYNC_TASK_SOURCE_ICONS: Partial<Record<string, BenchlingIconName>> = {
  // Nucleus has no sequence-primer icon; this is the same glyph used for primer pairs.
  [ModelTypeConstants.PREDICTION_BATCH]: 'arrow-right-arrow-left',
  [ModelTypeConstants.CHAT_SESSION]: 'sparkle-outline',
  [ModelTypeConstants.ANALYSIS]: 'file-import',
};

/** Icon for a homepage "Running now" async task row, based on the task's source object type. */
export function getHomepageAsyncTaskIcon(sourceObjectApiId: string | null): BenchlingIconName | undefined {
  if (sourceObjectApiId == null) {
    return undefined;
  }
  const modelType = APIUtil.apiIdToMaybeType(sourceObjectApiId);
  return modelType != null ? ASYNC_TASK_SOURCE_ICONS[modelType] : undefined;
}

/** Outline-style worklist icons for the homepage Worklists section. */
export function getHomepageWorklistOutlineIcon(worklistType: WorklistType): BenchlingIconName {
  return worklistType === WorklistType.BIOENTITY
    ? 'square-list-outline'
    : WORKLIST_TYPE_TO_ICON[worklistType];
}
